import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DisbursementType } from 'features/Payment/@types/disbursement'
import {
  SLICE_NAME,
  fetchDisbursementList,
  fetchBankList,
  fetchHubList,
  fetchDowloadDisbursement,
  updateDisbursementV2,
  bulkUpdateDisbursementV2,
} from './disbursementThunk'

export type { DisbursementType }

export interface QueryStateType {
  date: [Date | null, Date | null]
  pageIndex: number
  pageSize: number
  id: string
  status: {
    name: string
    value: string
  }
  customerId: string
  location: {
    name: string
    id: number
  }
}

export type BankListDataType = {
  name: string
  target_type: string
  target_channel: string
}

export type FormModalReprocessingType = {
  bank: BankListDataType
  accountName: string
  accountNumber: string
}

export type HubListType = {
  name: string
  id: number
}

export type ModalType = {
  mode: '' | 'BULK_UPDATE_STATUS' | 'UPDATE_STATUS'
  selectedSingleDisbursement: DisbursementType | ObjectNullType
  bulkStatusUpdatedTo: string
}
export interface DisbursementStateType {
  isLoading: boolean
  isShowModalReprocessing: boolean
  disbursement: DisbursementType[]
  query: QueryStateType
  selectedReprocessingData: DisbursementType | ObjectNullType
  bankListData: BankListDataType[]
  formModalReprocessing: FormModalReprocessingType
  hubList: HubListType[]
  selectedDisbursement: DisbursementType[]
  modal: ModalType
}

export const initialState: DisbursementStateType = {
  isLoading: false,
  disbursement: [],
  query: {
    date: [null, null],
    pageIndex: 1,
    pageSize: 20,
    status: { name: '', value: '' },
    customerId: '',
    id: '',
    location: { id: 0, name: '' },
  },
  isShowModalReprocessing: false,
  selectedReprocessingData: {},
  bankListData: [],
  formModalReprocessing: {
    bank: { name: '', target_channel: '', target_type: '' },
    accountName: '',
    accountNumber: '',
  },
  hubList: [],
  selectedDisbursement: [],
  modal: {
    mode: '',
    selectedSingleDisbursement: {},
    bulkStatusUpdatedTo: '',
  },
}

export type SetQueryPayloadType = {
  name: keyof QueryStateType
  value: QueryStateType[keyof QueryStateType]
}

export type SetFormModalReprocessingPayloadType = {
  name: keyof FormModalReprocessingType
  value: FormModalReprocessingType[keyof FormModalReprocessingType]
}

export type SetModalPayloadActionType = Partial<ModalType>

const disbursement = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    resetFormModalReprocessing: (state) => {
      state.formModalReprocessing = initialState.formModalReprocessing
    },
    resetModal: (state) => {
      state.modal = initialState.modal
    },
    resetSelectedDisbursement: (state) => {
      state.selectedDisbursement = initialState.selectedDisbursement
    },
    setQuery: (state, action: PayloadAction<SetQueryPayloadType>) => {
      const { value, name } = action.payload

      state.query[name] = value as never
    },
    setIsShowModalReprocessing: (state, action: PayloadAction<{ isShow: boolean }>) => {
      const { isShow } = action.payload
      state.isShowModalReprocessing = isShow
      if (!isShow) {
        state.selectedReprocessingData = initialState.selectedReprocessingData
      }
    },
    setSelectedReprocessingData: (state, action: PayloadAction<{ index: number }>) => {
      const { index } = action.payload
      state.selectedReprocessingData = state.disbursement[index]
    },
    setFormModalReprocessing: (
      state,
      action: PayloadAction<SetFormModalReprocessingPayloadType>,
    ) => {
      const { name, value } = action.payload
      state.formModalReprocessing[name] = value as never
    },
    addSelectedDisbursement: (state, action: PayloadAction<{ data: DisbursementType[] }>) => {
      state.selectedDisbursement = [...state.selectedDisbursement, ...action.payload.data]

      state.disbursement = state.disbursement.map((item) => {
        if (action.payload.data.some((d) => d.id === item.id)) {
          return { ...item, isSelected: true }
        }
        return item
      })
    },
    removeSelectedDisbursement: (state, action: PayloadAction<{ data: DisbursementType[] }>) => {
      state.selectedDisbursement = state.selectedDisbursement.filter(
        (item) => !action.payload.data.some((d) => d.id === item.id),
      )
      state.disbursement = state.disbursement.map((item) => {
        if (action.payload.data.some((d) => d.id === item.id)) {
          return { ...item, isSelected: false }
        }
        return item
      })
    },
    setModal: (state, action: PayloadAction<SetModalPayloadActionType>) => {
      state.modal = { ...state.modal, ...action.payload }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDisbursementList.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchDisbursementList.fulfilled, (state, action) => {
        const { data } = action.payload
        state.isLoading = false

        state.disbursement = data.disbursements.map((el) => ({
          ...el,
          uniqueId: window.crypto.randomUUID(),
          isSelected: state.selectedDisbursement.some((item) => item.id === el.id),
          disbursementAvailableStatusOptions: el.available_status_list.map((status) => ({
            name: status.toLowerCase(),
            value: status,
          })),
        }))
        state.query = {
          ...state.query,
          pageSize: data.pagination_response.limit,
        }
      })
      .addCase(fetchDisbursementList.rejected, (state) => {
        state.isLoading = false
        state.disbursement = []
      })
      .addCase(fetchBankList.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchBankList.fulfilled, (state, action) => {
        const { data } = action.payload
        state.isLoading = false
        state.bankListData = data
      })
      .addCase(fetchBankList.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchHubList.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchHubList.fulfilled, (state, action) => {
        const { data } = action.payload
        state.isLoading = false
        state.hubList = data.map((el) => ({ id: el.location_id, name: el.location_name }))
      })
      .addCase(fetchHubList.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchDowloadDisbursement.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchDowloadDisbursement.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(fetchDowloadDisbursement.rejected, (state) => {
        state.isLoading = false
      })

      .addCase(updateDisbursementV2.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateDisbursementV2.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(updateDisbursementV2.rejected, (state) => {
        state.isLoading = false
      })

      .addCase(bulkUpdateDisbursementV2.pending, (state) => {
        state.isLoading = true
      })
      .addCase(bulkUpdateDisbursementV2.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(bulkUpdateDisbursementV2.rejected, (state) => {
        state.isLoading = false
      })
  },
})

export const {
  reset,
  setQuery,
  setIsShowModalReprocessing,
  setSelectedReprocessingData,
  setFormModalReprocessing,
  resetFormModalReprocessing,
  setModal,
  addSelectedDisbursement,
  removeSelectedDisbursement,
  resetModal,
  resetSelectedDisbursement,
} = disbursement.actions

export default disbursement.reducer
